import { BlinkChallenge } from './challenges/blink';
import {
  HeadTurnLeftChallenge,
  HeadTurnRightChallenge,
} from './challenges/head-turn';
import { MouthOpenChallenge } from './challenges/mouth-open';
import { PitchDownChallenge } from './challenges/pitch-down';
import { PitchUpChallenge } from './challenges/pitch-up';
import { DEFAULT_CHALLENGE_CONFIG } from './constants';
import { FaceDetector } from './detectors/face-detector';
import { FrameAnalyzer } from './detectors/frame-analyzer';
import { Scheduler } from './scheduler';
import type {
  ChallengeConfig,
  ChallengeType,
  ChallengeResult,
  LivenessReport,
} from './types';
import { Validator } from './validator';

export class LivenessEngine {
  private frameAnalyzer: FrameAnalyzer | null = null;

  private scheduler: Scheduler | null = null;

  private validator: Validator | null = null;

  private results: ChallengeResult[] = [];

  private consecutiveFailures = 0;

  private config: ChallengeConfig;

  private candidateId = '';

  private certificationLogId = '';

  private isPaused = false;

  onChallengeStart?: (type: ChallengeType) => void;

  onWarmUpEnd?: () => void;

  onChallengeEnd?: (result: ChallengeResult) => void;

  onProgress?: (progress: number) => void;

  onBlocked?: () => void;

  onReport?: (report: LivenessReport) => void;

  constructor(config?: Partial<ChallengeConfig>) {
    this.config = { ...DEFAULT_CHALLENGE_CONFIG, ...config };
  }

  start(
    stream: MediaStream,
    candidateId: string,
    certificationLogId: string,
    config?: Partial<ChallengeConfig>,
  ): void {
    this.candidateId = candidateId;
    this.certificationLogId = certificationLogId;

    if (config) {
      this.config = { ...this.config, ...config };
    }

    const faceDetector = new FaceDetector();

    this.frameAnalyzer = new FrameAnalyzer({
      fps: 30,
      detectors: [faceDetector],
      onFrame: frame => {
        if (this.validator) {
          this.validator.validateFrame(frame);
        }
      },
    });

    this.frameAnalyzer.start(stream);

    this.scheduler = new Scheduler(this.config, type => {
      this.handleChallengeScheduled(type);
    });

    this.scheduler.start();
  }

  pause(): void {
    this.isPaused = true;
    if (this.scheduler) {
      this.scheduler.pause();
    }
    if (this.frameAnalyzer) {
      this.frameAnalyzer.pause();
    }
  }

  resume(): void {
    this.isPaused = false;
    if (this.scheduler) {
      this.scheduler.resume();
    }
    if (this.frameAnalyzer) {
      this.frameAnalyzer.resume();
    }
  }

  stop(): LivenessReport {
    if (this.scheduler) {
      this.scheduler.stop();
      this.scheduler = null;
    }

    if (this.validator) {
      this.validator.stop();
      this.validator = null;
    }

    if (this.frameAnalyzer) {
      this.frameAnalyzer.stop();
      this.frameAnalyzer = null;
    }

    return this.buildReport();
  }

  private handleChallengeScheduled(type: ChallengeType): void {
    if (this.isPaused || !this.frameAnalyzer) {
      return;
    }

    let challenge;
    switch (type) {
      case 'head_turn_left':
        challenge = new HeadTurnLeftChallenge();
        break;
      case 'head_turn_right':
        challenge = new HeadTurnRightChallenge();
        break;
      case 'pitch_up':
        challenge = new PitchUpChallenge();
        break;
      case 'pitch_down':
        challenge = new PitchDownChallenge();
        break;
      case 'blink':
        challenge = new BlinkChallenge();
        break;
      case 'mouth_open':
        challenge = new MouthOpenChallenge();
        break;
      default:
        return;
    }

    challenge.onWarmUpEnd = () => {
      if (this.onWarmUpEnd) {
        this.onWarmUpEnd();
      }
    };

    this.validator = new Validator({
      challenge,
      frameAnalyzer: this.frameAnalyzer,
      onResult: result => {
        this.handleChallengeResult(result);
      },
      onProgress: progress => {
        if (this.onProgress) this.onProgress(progress);
      },
    });

    this.validator.start(this.config.timeoutSeconds * 1000);

    if (this.onChallengeStart) {
      this.onChallengeStart(type);
    }
  }

  private handleChallengeResult(result: ChallengeResult): void {
    this.results.push(result);

    if (result.status === 'failed' || result.status === 'timeout') {
      this.consecutiveFailures++;

      if (this.consecutiveFailures >= this.config.maxConsecutiveFailures) {
        if (this.scheduler) {
          this.scheduler.stop();
          this.scheduler = null;
        }
        if (this.validator) {
          this.validator.stop();
          this.validator = null;
        }
        if (this.onReport) {
          this.onReport(this.buildReport());
        }
        if (this.onBlocked) {
          this.onBlocked();
        }
        return;
      }
    } else if (result.status === 'passed') {
      this.consecutiveFailures = 0;
    }

    if (this.onChallengeEnd) {
      this.onChallengeEnd(result);
    }

    if (this.onReport) {
      this.onReport(this.buildReport());
    }
  }

  private buildReport(): LivenessReport {
    const passed = this.results.filter(r => r.status === 'passed').length;
    const failed = this.results.filter(r => r.status === 'failed').length;
    const timeouts = this.results.filter(r => r.status === 'timeout').length;

    const totalAttempts = this.results.length || 1;
    const livenessScore = (passed / totalAttempts) * 100;

    const flagged =
      this.consecutiveFailures >= this.config.maxConsecutiveFailures ||
      failed > totalAttempts * 0.3;

    return {
      candidateId: this.candidateId,
      certificationLogId: this.certificationLogId,
      totalChallenges: this.results.length,
      passed,
      failed,
      timeouts,
      challenges: this.results,
      livenessScore: Math.round(livenessScore * 10) / 10,
      flagged,
    };
  }
}

export * from './types';
export * from './constants';

export const TEST_CHALLENGE_CONFIG = {
  minIntervalMinutes: 0,
  maxIntervalMinutes: 0.5,
  firstChallengeMinMinutes: 0,
  firstChallengeMaxMinutes: 0.2,
  timeoutSeconds: 15,
  maxConsecutiveFailures: 3,
};
